import { queryBookingOnboardingSubmissions } from '@/_apis_/booking/axios';
import type { IBookingOnboardingSubmission } from '@/_apis_/booking/type';
import { BookingFormsTable, BookingOnboardingTableItem } from '@/components/_dashboard/forms/BookingFormsTable';
import { formatFormSubmissionDate, renderOptionalFormValue } from '@/components/_dashboard/forms/formUtils';
import HeaderBreadcrumbs from '@/components/HeaderBreadcrumbs';
import Page from '@/components/Page';
import useSettings from '@/hooks/useSettings';
import { PATH_DASHBOARD } from '@/routes/paths';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, Container } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PublicFormLinkCard from './PublicFormLinkCard';

const BOOKING_ONBOARDING_LOAD_ERROR = 'Failed to load booking onboarding submissions.';

const getSubmissionId = (submission: IBookingOnboardingSubmission, index: number): string => {
    return submission.submissionId || `booking-onboarding-${index}`;
};

const getTimestampValue = (value: IBookingOnboardingSubmission['createdAt']): number | null => {
    if (typeof value === 'number') return value;
    if (value?._seconds !== undefined) return value._seconds;
    return null;
};

interface LoadBookingOnboardingSubmissionsOptions {
    querySubmissions: typeof queryBookingOnboardingSubmissions;
    setSubmissionList: (submissionList: IBookingOnboardingSubmission[]) => void;
    setErrorMessage: (errorMessage: string | null) => void;
    setIsLoading: (isLoading: boolean) => void;
}

export const loadBookingOnboardingSubmissions = async ({
    querySubmissions,
    setSubmissionList,
    setErrorMessage,
    setIsLoading
}: LoadBookingOnboardingSubmissionsOptions): Promise<void> => {
    setIsLoading(true);
    setErrorMessage(null);

    try {
        const response = await querySubmissions();

        if (!response?.data) {
            setSubmissionList([]);
            setErrorMessage(response?.message || BOOKING_ONBOARDING_LOAD_ERROR);
            return;
        }

        setSubmissionList(response.data);
    } catch {
        setSubmissionList([]);
        setErrorMessage(BOOKING_ONBOARDING_LOAD_ERROR);
    } finally {
        setIsLoading(false);
    }
};

export default function BookingFormList(): JSX.Element {
    const { themeStretch } = useSettings();
    const { t } = useTranslation();
    const [submissionList, setSubmissionList] = useState<IBookingOnboardingSubmission[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const loadSubmissions = useCallback(async (): Promise<void> => {
        await loadBookingOnboardingSubmissions({
            querySubmissions: queryBookingOnboardingSubmissions,
            setSubmissionList,
            setErrorMessage,
            setIsLoading
        });
    }, []);

    useEffect(() => {
        void loadSubmissions();
    }, [loadSubmissions]);

    const tableItems = useMemo<BookingOnboardingTableItem[]>(() => {
        return submissionList.map((submission, index) => {
            const createdAt = getTimestampValue(submission.createdAt);
            const updatedAt = getTimestampValue(submission.updatedAt);

            return {
                id: getSubmissionId(submission, index),
                restaurantName: renderOptionalFormValue(submission.restaurantName),
                email: renderOptionalFormValue(submission.email),
                contact: renderOptionalFormValue(submission.contact),
                ownerManagerName: renderOptionalFormValue(submission.ownerManagerName),
                diningDuration: renderOptionalFormValue(submission.diningDuration),
                bookingSlotInterval: renderOptionalFormValue(submission.bookingSlotInterval),
                minimumAdvanceBookingTime: renderOptionalFormValue(submission.minimumAdvanceBookingTime),
                sessionMaxCapacity: renderOptionalFormValue(submission.sessionMaxCapacity),
                timezone: renderOptionalFormValue(submission.timezone),
                bookingType: renderOptionalFormValue(submission.bookingType),
                maxPaxPerBooking: renderOptionalFormValue(submission.maxPaxPerBooking),
                createdAt,
                createdAtDisplay: formatFormSubmissionDate(createdAt),
                updatedAt,
                updatedAtDisplay: formatFormSubmissionDate(updatedAt),
                rawSubmission: submission
            };
        });
    }, [submissionList]);

    const customerHeader = (
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.25 }}>
            <Button
                variant="outlined"
                color="inherit"
                startIcon={<RefreshIcon />}
                disabled={isLoading}
                onClick={() => {
                    void loadSubmissions();
                }}
            >
                Refresh
            </Button>
        </Box>
    );

    return (
        <Page title={t('Booking Forms')}>
            <Container maxWidth={themeStretch ? false : 'lg'}>
                <HeaderBreadcrumbs
                    heading={t('Booking Forms')}
                    links={[
                        { name: t('Dash Board'), href: PATH_DASHBOARD.root },
                        { name: t('Forms'), href: PATH_DASHBOARD.forms.root },
                        { name: t('Booking') }
                    ]}
                />
                <PublicFormLinkCard
                    formPath={PATH_DASHBOARD.public.bookingOnboarding()}
                    title="Share the booking onboarding form"
                    description="Send this link to restaurants that need to provide booking setup details."
                />
                {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
                {!errorMessage && (
                    <BookingFormsTable submissionList={tableItems} isLoading={isLoading} customerHeader={customerHeader} />
                )}
            </Container>
        </Page>
    );
}
